
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faCheck,
  faCircleCheck,
  faCircleExclamation,
  faDownload,
  faFileExcel,
  faFolderOpen,
  faHourglassHalf,
  faPlus,
  faTicket,
  faClock,
  faArrowsRotate,
  faSpinner,
  faFilter,
  faCalendarDays,
  faUsers,
  faArrowRight,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

import Prince from "../../Components/Prince";
import TopHeader from "../../Components/TopHeader";
import Loader from "../../assets/img/loader.gif";
import "../../assets/css/casedashboard.css";
import * as caseService from "../../Services/caseService";

// Change this route if your B2C dashboard route is different.
const B2C_DASHBOARD_ROUTE = "/b2c/cases-dashboard";

const departments = [
  "Tech Support",
  "First Mile Ops",
  "Weight Team",
  "Legal",
  "Customer Support",
  "Management",
];

const statuses = [
  "Open",
  "In Progress",
  "Pending on Client",
  "Resolved",
  "Closed",
];

const statusColors = {
  Open: "#ef4444",
  "In Progress": "#3b82f6",
  "Pending on Client": "#f59e0b",
  Resolved: "#10b981",
  Closed: "#64748b",
};

const formatNumber = (value = 0) =>
  Number(value || 0).toLocaleString("en-IN");

const normalizeStatus = (status) =>
  status === "New" ? "Open" : status || "Unknown";

const getAge = (date) => {
  if (!date) return 0;

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return 0;

  return Math.max(
    0,
    Math.floor((Date.now() - parsedDate.getTime()) / 86400000)
  );
};

const getEmployeeName = (employee) => {
  if (!employee) return "";

  if (typeof employee === "string") return employee;

  return (
    employee.employeeName ||
    employee.name ||
    [employee.firstName, employee.lastName]
      .filter(Boolean)
      .join(" ") ||
    employee.email ||
    ""
  );
};

const normalizeCase = (item) => {
  const sourceAssignments =
    item?.EmployeeCasesM ??
    item?.assignments ??
    item?.activeAssignments ??
    item?.departmentStatusGroups ??
    [];

  const assignments = Array.isArray(sourceAssignments)
    ? sourceAssignments.map((assignment) => {
        const emp = assignment?.employee;

        return {
          ...assignment,
          department:
            emp?.department ||
            emp?.departmentName ||
            assignment?.department ||
            assignment?.departmentName ||
            "",
          employee:
            getEmployeeName(emp) ||
            assignment?.employeeName ||
            (typeof assignment?.employee === "string"
              ? assignment.employee
              : ""),
          employeeId:
            assignment?.employeeId ??
            emp?.id ??
            emp?.employeeId ??
            "",
          status: normalizeStatus(
            assignment?.status ||
              assignment?.myStatus ||
              assignment?.overallStatus ||
              item?.status
          ),
          statusAt:
            assignment?.statusAt ||
            assignment?.updatedAt ||
            assignment?.createdAt ||
            item?.updatedAt ||
            item?.createdAt ||
            null,
        };
      })
    : [];

  const customer =
    item?.customer && typeof item.customer === "object"
      ? item.customer
      : item?.customerDetails &&
          typeof item.customerDetails === "object"
        ? item.customerDetails
        : {};

  const customerName =
    customer?.companyName ||
    customer?.customerName ||
    customer?.name ||
    (typeof item?.customer === "string" ? item.customer : "") ||
    item?.customerName ||
    item?.companyName ||
    "-";

  const awbValue = item?.awbNo ?? item?.awb;
  const awbList = Array.isArray(awbValue)
    ? awbValue
    : [awbValue];

  const awb = [
    ...new Set(
      awbList
        .map((value) => {
          if (value == null || value === "") return "";

          if (typeof value === "object") {
            return (
              value.awbNo ||
              value.awbNumber ||
              value.number ||
              ""
            );
          }

          return String(value);
        })
        .map((value) => String(value).trim())
        .filter(Boolean)
    ),
  ].join(", ");

  return {
    ...item,
    id: item?.id ?? item?.caseId ?? item?.casesMId ?? "-",
    awb: awb || "-",
    customer: customerName,
    customerName,
    companyName: customerName,
    accountType: item?.accountType || customer?.accountType || "",
    department:
      item?.department ||
      item?.departmentName ||
      assignments[0]?.department ||
      "",
    employee:
      getEmployeeName(item?.employee) ||
      item?.employeeName ||
      assignments[0]?.employee ||
      "",
    status: normalizeStatus(
      item?.status ||
        item?.overallStatus ||
        item?.myStatus ||
        item?.masterStatus
    ),
    createdAt: item?.createdAt || item?.createdOn || null,
    statusAt:
      item?.statusAt ||
      item?.statusUpdatedAt ||
      item?.updatedAt ||
      item?.createdAt ||
      null,
    tat: item?.tat ?? item?.isTat ?? "—",
    assignments,
  };
};

const getAssignmentStatusDate = (assignment, item) =>
  assignment?.statusAt ||
  assignment?.updatedAt ||
  assignment?.createdAt ||
  item?.statusAt ||
  item?.createdAt;

const isWithinDateRange = (date, from, to) => {
  if (!from && !to) return true;
  if (!date) return false;

  const itemDate = new Date(date);

  if (Number.isNaN(itemDate.getTime())) return false;

  // Compare date-only values to avoid time-zone boundary issues.
  const dateValue = itemDate.toISOString().slice(0, 10);

  if (from && dateValue < from) return false;
  if (to && dateValue > to) return false;

  return true;
};

const getStatusClass = (status) =>
  `bcd-status-${String(status || "")
    .toLowerCase()
    .replace(/\s+/g, "-")}`;

export const B2BCasesDashboard = () => {
  const navigate = useNavigate();

  const [cases, setCases] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Dashboard filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [status, setStatus] = useState("");

  const loadDashboardData = useCallback(async () => {
    setLoading(true);

    try {
      const res = await caseService.getB2BCasesDashboard({
        page: 1,
        pageSize: 100,
      });

      const response = res?.data;

      if (response?.success !== true) {
        setCases([]);
        setSummaryData(null);

        console.error(
          "B2B dashboard API error:",
          response?.message || "Unable to fetch dashboard data"
        );

        return;
      }

      const responseData = Array.isArray(response.data)
        ? response.data
        : [];

      const normalizedCases = responseData
        .map(normalizeCase)
        .filter((item) => {
          const accountType = String(
            item.accountType || ""
          ).toUpperCase();

          return (
            !accountType ||
            accountType === "B2B" ||
            accountType === "BOTH"
          );
        });

      setCases(normalizedCases);

      const counts = response.summary || {};

      setSummaryData({
        totalCases: Number(
          counts.total ??
            response.pagination?.totalRecords ??
            normalizedCases.length
        ),
        statusCounts: {
          Open: Number(counts.new ?? 0),
          "In Progress": Number(counts.inProgress ?? 0),
          "Pending on Client": Number(
            counts.pendingOnClient ?? 0
          ),
          Resolved: Number(counts.resolved ?? 0),
          Closed: Number(counts.closed ?? 0),
        },
      });
    } catch (error) {
      console.error(
        "Failed to load B2B dashboard:",
        error?.response?.data || error
      );

      setCases([]);
      setSummaryData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Employee dropdown options from case assignments.
  const employeeOptions = useMemo(() => {
    const employeeMap = new Map();

    cases.forEach((item) => {
      (item.assignments || []).forEach((assignment) => {
        const name = assignment.employee;

        if (!name) return;

        const id = String(
          assignment.employeeId || name
        );

        if (!employeeMap.has(id)) {
          employeeMap.set(id, {
            id,
            name,
          });
        }
      });

      if (item.employee) {
        const id = String(item.employee);

        if (!employeeMap.has(id)) {
          employeeMap.set(id, {
            id,
            name: item.employee,
          });
        }
      }
    });

    return Array.from(employeeMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [cases]);

  // Apply date and employee filters.
  const filteredCases = useMemo(() => {
    return cases.filter((item) => {
      const dateMatched = isWithinDateRange(
        item.createdAt,
        fromDate,
        toDate
      );

      if (!dateMatched) return false;

      if (selectedEmployee) {
        const assignedEmployees = (item.assignments || [])
          .map((assignment) => [
            String(assignment.employeeId || ""),
            String(assignment.employee || ""),
          ])
          .flat();

        const employeeMatched =
          assignedEmployees.includes(selectedEmployee) ||
          String(item.employee || "") === selectedEmployee;

        if (!employeeMatched) return false;
      }

      return true;
    });
  }, [cases, fromDate, toDate, selectedEmployee]);

  const accountCases = useMemo(
    () =>
      filteredCases.filter((item) => {
        const accountType = String(
          item.accountType || ""
        ).toUpperCase();

        return (
          !accountType ||
          accountType === "B2B" ||
          accountType === "BOTH"
        );
      }),
    [filteredCases]
  );

  const hasFilters = Boolean(
    fromDate || toDate || selectedEmployee
  );

  const totalCases = hasFilters
    ? accountCases.length
    : summaryData?.totalCases ?? accountCases.length;

  const statusCounts = useMemo(() => {
    const counts = {};

    statuses.forEach((name) => {
      counts[name] = accountCases.filter(
        (item) => item.status === name
      ).length;
    });

    return counts;
  }, [accountCases]);

  const getStatusCount = (name) => {
    // Use API summary when no local filters are selected.
    if (!hasFilters && summaryData?.statusCounts) {
      return summaryData.statusCounts[name] ?? statusCounts[name] ?? 0;
    }

    return statusCounts[name] || 0;
  };

  // Dynamic donut chart based on actual status counts.
  const donutBackground = useMemo(() => {
    const total = statuses.reduce(
      (sum, name) => sum + getStatusCount(name),
      0
    );

    if (!total) return "#e8eef6";

    let currentPercentage = 0;

    const segments = statuses.map((name) => {
      const percentage =
        (getStatusCount(name) / total) * 100;

      const start = currentPercentage;
      currentPercentage += percentage;

      return `${statusColors[name]} ${start}% ${currentPercentage}%`;
    });

    return `conic-gradient(${segments.join(", ")})`;
  }, [statusCounts, summaryData, hasFilters]);

  const departmentAging = useMemo(
    () =>
      departments.map((departmentName) => {
        const ages = accountCases.flatMap((item) => {
          const assignments = item.assignments || [];

          if (assignments.length > 0) {
            return assignments
              .filter(
                (assignment) =>
                  assignment.department === departmentName &&
                  assignment.status === "In Progress"
              )
              .map((assignment) =>
                getAge(getAssignmentStatusDate(assignment, item))
              );
          }

          if (
            item.department === departmentName &&
            item.status === "In Progress"
          ) {
            return [
              getAge(item.statusAt || item.createdAt),
            ];
          }

          return [];
        });

        return {
          department: departmentName,
          oneDay: ages.filter((age) => age <= 1).length,
          twoDays: ages.filter(
            (age) => age >= 0 && age <= 2
          ).length,
          threeToSeven: ages.filter(
            (age) => age >= 3 && age <= 7
          ).length,
          eightPlus: ages.filter((age) => age >= 8).length,
        };
      }),
    [accountCases]
  );

  const exportCsv = () => {
    const headers = [
      "Case ID",
      "AWB",
      "Customer",
      "Account Type",
      "Department",
      "Employee",
      "Status",
      "Case Age Days",
      "Status Age Days",
      "SLA/TAT",
    ];

    const rows = accountCases
      .filter((item) => !status || item.status === status)
      .map((item) => {
        const assignments = item.assignments || [];

        return [
          item.id,
          item.awb,
          item.customer,
          item.accountType || "B2B",
          assignments
            .map((assignment) => assignment.department)
            .filter(Boolean)
            .join(" | ") || item.department,
          assignments
            .map((assignment) => assignment.employee)
            .filter(Boolean)
            .join(" | ") || item.employee,
          item.status,
          getAge(item.createdAt),
          getAge(item.statusAt),
          item.tat,
        ];
      });

    const csv = [headers, ...rows]
      .map((row) =>
        row
          .map(
            (value) =>
              `"${String(value ?? "").replace(/"/g, '""')}"`
          )
          .join(",")
      )
      .join("\n");

    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "b2b-cases-dashboard.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleStatusCardClick = (selectedStatus) => {
    setStatus((current) =>
      current === selectedStatus ? "" : selectedStatus
    );
  };

  const handleResetFilters = () => {
    setFromDate("");
    setToDate("");
    setSelectedEmployee("");
    setStatus("");
  };

  const handleB2CSwitch = () => {
    navigate(B2C_DASHBOARD_ROUTE);
  };

  return (
    <div className="pages bcd-page">
      <TopHeader />
      <Prince />

      {loading && (
        <div className="loading-overlay">
          <img src={Loader} alt="Loading..." />
        </div>
      )}

      <div className="bcd-dashboard">
        {/* PAGE HEADING */}
        <div className="bcd-heading-row">
          <div>
            <h2>
              <FontAwesomeIcon
                icon={faTicket}
                className="me-2"
              />
              B2B Case Dashboard
            </h2>

            <p>
              Track, manage and resolve B2B customer cases efficiently.
            </p>
          </div>

          <button
            type="button"
            className="bcd-btn bcd-btn-primary"
            disabled={loading}
            onClick={loadDashboardData}
          >
            <FontAwesomeIcon
              icon={loading ? faSpinner : faArrowsRotate}
              spin={loading}
            />
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {/* TOP FILTERS: DATE, EMPLOYEE, B2B/B2C */}
        <section className="bcd-top-controls">
          <div className="bcd-top-control bcd-date-control">
            <label>
              <FontAwesomeIcon icon={faCalendarDays} />
              From Date
            </label>

            <input
              type="date"
              value={fromDate}
              max={toDate || undefined}
              onChange={(event) =>
                setFromDate(event.target.value)
              }
            />
          </div>

          <div className="bcd-top-control bcd-date-control">
            <label>
              <FontAwesomeIcon icon={faCalendarDays} />
              To Date
            </label>

            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(event) =>
                setToDate(event.target.value)
              }
            />
          </div>

          <div className="bcd-top-control bcd-employee-control">
            <label>
              <FontAwesomeIcon icon={faUsers} />
              Employee
            </label>

            <select
              value={selectedEmployee}
              onChange={(event) =>
                setSelectedEmployee(event.target.value)
              }
            >
              <option value="">All Employees</option>

              {employeeOptions.map((employee) => (
                <option
                  key={employee.id}
                  value={employee.id}
                >
                  {employee.name}
                </option>
              ))}
            </select>
          </div>

            <button
            type="button"
            className="bcd-btn bcd-btn-light bcd-reset-btn"
            onClick={handleResetFilters}
          >
            Reset Filters
          </button>

          <div className="bcd-account-switch">
            <button
              type="button"
              className="active"
              aria-current="page"
            >
              B2B
            </button>

            <button
              type="button"
              onClick={handleB2CSwitch}
            >
              B2C
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>

        
        </section>

        {/* SUMMARY CARDS */}
        <section className="bcd-summary-grid">
          <div className="bcd-summary-card navy">
            <div className="bcd-summary-icon">
              <FontAwesomeIcon icon={faFolderOpen} />
            </div>

            <div className="bcd-summary-content">
              <span>Total B2B Cases</span>
              <strong>{formatNumber(totalCases)}</strong>
              <small>Cases in selected filters</small>
            </div>
          </div>

          <div
            className={`bcd-summary-card red ${
              status === "Open" ? "selected" : ""
            }`}
            onClick={() => handleStatusCardClick("Open")}
          >
            <div className="bcd-summary-icon">
              <FontAwesomeIcon icon={faFolderOpen} />
            </div>

            <div className="bcd-summary-content">
              <span>New / Open</span>
              <strong>{formatNumber(getStatusCount("Open"))}</strong>
              <small>New cases</small>
            </div>
          </div>

          <div
            className={`bcd-summary-card blue ${
              status === "In Progress" ? "selected" : ""
            }`}
            onClick={() =>
              handleStatusCardClick("In Progress")
            }
          >
            <div className="bcd-summary-icon">
              <FontAwesomeIcon icon={faClock} />
            </div>

            <div className="bcd-summary-content">
              <span>In Progress</span>
              <strong>
                {formatNumber(getStatusCount("In Progress"))}
              </strong>
              <small>Cases in progress</small>
            </div>
          </div>

          <div
            className={`bcd-summary-card orange ${
              status === "Pending on Client" ? "selected" : ""
            }`}
            onClick={() =>
              handleStatusCardClick("Pending on Client")
            }
          >
            <div className="bcd-summary-icon">
              <FontAwesomeIcon icon={faHourglassHalf} />
            </div>

            <div className="bcd-summary-content">
              <span>Pending on Client</span>
              <strong>
                {formatNumber(getStatusCount("Pending on Client"))}
              </strong>
              <small>Awaiting client response</small>
            </div>
          </div>

          <div
            className={`bcd-summary-card green ${
              status === "Resolved" ? "selected" : ""
            }`}
            onClick={() => handleStatusCardClick("Resolved")}
          >
            <div className="bcd-summary-icon">
              <FontAwesomeIcon icon={faCircleCheck} />
            </div>

            <div className="bcd-summary-content">
              <span>Resolved</span>
              <strong>
                {formatNumber(getStatusCount("Resolved"))}
              </strong>
              <small>Resolved cases</small>
            </div>
          </div>

          <div
            className={`bcd-summary-card slate ${
              status === "Closed" ? "selected" : ""
            }`}
            onClick={() => handleStatusCardClick("Closed")}
          >
            <div className="bcd-summary-icon">
              <FontAwesomeIcon icon={faCheck} />
            </div>

            <div className="bcd-summary-content">
              <span>Closed</span>
              <strong>
                {formatNumber(getStatusCount("Closed"))}
              </strong>
              <small>Closed cases</small>
            </div>
          </div>
        </section>

        {/* CASE AGING + STATUS DISTRIBUTION */}
        <section className="bcd-analytics-grid">
          {/* CASE AGING */}
          <div className="bcd-panel">
            <div className="bcd-panel-heading">
              <h3>
                <FontAwesomeIcon
                  icon={faHourglassHalf}
                  className="me-2"
                />
                Case Aging (B2B)
              </h3>
            </div>

            <p className="bcd-panel-description">
              Department-wise In Progress cases aging
            </p>

            <div className="bcd-mini-table-wrap">
              <table className="bcd-mini-table">
                <thead>
                  <tr>
                    <th>Department</th>
                    <th>0–1 Days</th>
                    <th>0–2 Days</th>
                    <th>3–7 Days</th>
                    <th>8+ Days</th>
                  </tr>
                </thead>

                <tbody>
                  {departmentAging.map((item) => (
                    <tr key={item.department}>
                      <td>{item.department}</td>

                      <td>
                        <strong>{item.oneDay}</strong>
                      </td>

                      <td>
                        <strong>{item.twoDays}</strong>
                      </td>

                      <td>
                        <strong
                          className={
                            item.threeToSeven > 0
                              ? "bcd-aging-warning"
                              : ""
                          }
                        >
                          {item.threeToSeven}
                        </strong>
                      </td>

                      <td>
                        <strong
                          className={
                            item.eightPlus > 0
                              ? "bcd-aging-critical"
                              : ""
                          }
                        >
                          {item.eightPlus}
                        </strong>
                      </td>
                    </tr>
                  ))}

                  {!loading && accountCases.length === 0 && (
                    <tr>
                      <td colSpan={5} className="bcd-empty-state">
                        No case aging data available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="bcd-aging-legend">
              <span>
                <i className="aging-dot normal" />
                0–2 Days
              </span>

              <span>
                <i className="aging-dot warning" />
                3–7 Days
              </span>

              <span>
                <i className="aging-dot critical" />
                8+ Days
              </span>
            </div>
          </div>

          {/* STATUS DISTRIBUTION */}
          <div className="bcd-panel bcd-distribution-panel">
            <div className="bcd-panel-heading">
              <h3>
                <FontAwesomeIcon
                  icon={faFilter}
                  className="me-2"
                />
                Case Distribution by Status
              </h3>

              {status && (
                <button
                  type="button"
                  className="bcd-link-button"
                  onClick={() => setStatus("")}
                >
                  Clear filter
                </button>
              )}
            </div>

            <p className="bcd-panel-description">
              Click any status below to filter the dashboard.
            </p>

            <div className="bcd-status-distribution">
              <div
                className="bcd-donut"
                style={{ background: donutBackground }}
              >
                <div>
                  <strong>{formatNumber(totalCases)}</strong>
                  <span>Total Cases</span>
                </div>
              </div>

              <div className="bcd-distribution-legend">
                {statuses.map((name) => (
                  <button
                    type="button"
                    key={name}
                    className={`bcd-legend-item ${
                      status === name ? "active" : ""
                    }`}
                    onClick={() =>
                      handleStatusCardClick(name)
                    }
                  >
                    <span
                      className="bcd-legend-dot"
                      style={{
                        background: statusColors[name],
                      }}
                    />

                    <span className="bcd-legend-name">
                      {name}
                    </span>

                    <strong>
                      {formatNumber(getStatusCount(name))}
                    </strong>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* QUICK ACTIONS */}
        <section className="bcd-panel bcd-quick-actions">
          <div className="bcd-panel-heading">
            <h3>Quick Actions</h3>
          </div>

          <div className="bcd-quick-grid">
            <button
              type="button"
              className="bcd-quick-card"
              onClick={() => {
                // Connect your existing Add Case modal or route here.
              }}
            >
              <span className="bcd-quick-icon add">
                <FontAwesomeIcon icon={faPlus} />
              </span>

              <span>
                <strong>Add Case</strong>
                <small>Create a new case</small>
              </span>
            </button>

            <button
              type="button"
              className="bcd-quick-card"
              onClick={() => {
                // Connect your existing Excel import modal or route here.
              }}
            >
              <span className="bcd-quick-icon excel">
                <FontAwesomeIcon icon={faFileExcel} />
              </span>

              <span>
                <strong>Import Excel</strong>
                <small>Bulk upload cases</small>
              </span>
            </button>

            <button
              type="button"
              className="bcd-quick-card"
              onClick={exportCsv}
            >
              <span className="bcd-quick-icon export">
                <FontAwesomeIcon icon={faDownload} />
              </span>

              <span>
                <strong>Export Report</strong>
                <small>Download filtered B2B data</small>
              </span>
            </button>

            <button
              type="button"
              className="bcd-quick-card"
              onClick={() => handleStatusCardClick("Closed")}
            >
              <span className="bcd-quick-icon closed">
                <FontAwesomeIcon icon={faCircleCheck} />
              </span>

              <span>
                <strong>Closed Cases</strong>
                <small>
                  {formatNumber(getStatusCount("Closed"))} closed cases
                </small>
              </span>
            </button>
          </div>

          <div className="bcd-info-box">
            <strong>
              <FontAwesomeIcon
                icon={faCircleExclamation}
                className="me-2"
              />
              Dashboard information
            </strong>

            <p>
              This dashboard displays B2B cases only. Date and
              employee filters are applied to the fetched cases.
              Status distribution and aging are updated according
              to the selected filters.
            </p>
          </div>
        </section>

        <div className="bcd-dashboard-footer">
          <span>
            <FontAwesomeIcon icon={faBuilding} />
            B2B Case Overview
          </span>

          <span>
            <FontAwesomeIcon icon={faUser} />
            {selectedEmployee
              ? `Employee: ${
                  employeeOptions.find(
                    (employee) => employee.id === selectedEmployee
                  )?.name || selectedEmployee
                }`
              : "All Employees"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default B2BCasesDashboard;
